import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { X, Search, Loader2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketWhatsappId }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId);
  const { findAll: findAllQueues } = useQueues();
  const { loadingWhatsapps, whatsApps } = useWhatsApps();
  const { user: loggedInUser } = useContext(AuthContext);

  useEffect(() => {
    const loadQueues = async () => {
      const list = await findAllQueues();
      setAllQueues(list);
      setQueues(list);
    };
    loadQueues();
  }, []);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) { setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/", { params: { searchParam } });
        setOptions(data.users);
        setLoading(false);
      } catch (err) { setLoading(false); toastError(err); }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParam, modalOpen]);

  const handleClose = () => { onClose(); setSearchParam(""); setSelectedUser(null); };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!ticketid) return;
    setLoading(true);
    try {
      const data = {};
      if (selectedUser) data.userId = selectedUser.id;
      if (selectedQueue) {
        data.queueId = selectedQueue;
        if (!selectedUser) { data.status = "pending"; data.userId = null; }
      }
      if (selectedWhatsapp) data.whatsappId = selectedWhatsapp;
      await api.put(`/tickets/${ticketid}`, data);
      setLoading(false);
      history.push("/tickets");
    } catch (err) { setLoading(false); toastError(err); }
  };

  if (!modalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{i18n.t("transferTicketModal.title")}</h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSaveTicket}>
          <div className="px-5 py-4 space-y-4">
            {/* User search */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("transferTicketModal.fieldLabel")}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
                <input
                  autoFocus
                  type="text"
                  placeholder={i18n.t("transferTicketModal.fieldLabel")}
                  value={selectedUser ? selectedUser.name : searchParam}
                  onChange={(e) => { setSearchParam(e.target.value); setSelectedUser(null); }}
                  className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              {options.length > 0 && !selectedUser && (
                <div className="mt-1 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                  {options.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => {
                        setSelectedUser(u);
                        setQueues(u.queues?.length ? u.queues : allQueues);
                        setSelectedQueue("");
                        setOptions([]);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      {u.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Queue */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("transferTicketModal.fieldQueueLabel")}</label>
              <select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                <option value="">{i18n.t("transferTicketModal.fieldQueuePlaceholder")}</option>
                {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </div>

            {/* WhatsApp connection */}
            <Can
              role={loggedInUser.profile}
              perform="ticket-options:transferWhatsapp"
              permissions={loggedInUser.permissions}
              yes={() => !loadingWhatsapps && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("transferTicketModal.fieldConnectionLabel")}</label>
                  <select
                    value={selectedWhatsapp}
                    onChange={(e) => setSelectedWhatsapp(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  >
                    {whatsApps.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              )}
            />
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
            <button type="button" onClick={handleClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              {i18n.t("transferTicketModal.buttons.cancel")}
            </button>
            <button type="submit" disabled={loading} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {i18n.t("transferTicketModal.buttons.ok")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferTicketModal;
